import './settings-leaf.css';
import { useTranslation } from 'react-i18next';
import type { Company } from '@shared/types/index.ts';
import {
  DEFAULT_SWAP_CUTOFF_HOURS,
  DEFAULT_SWAP_POLICY,
  MAX_SWAP_CUTOFF_HOURS,
  SWAP_POLICIES,
  type SwapPolicy,
} from '@shared/shift-swaps.ts';
import { useCompanyCardForm, CardSaveFooter } from './company-card-form.tsx';
import { Select } from '../ui.tsx';
import { NumberInput } from '../number-input.tsx';

const snapshot = (company: Company) => ({
  swapPolicy: company.swapPolicy ?? DEFAULT_SWAP_POLICY,
  swapCutoffHours: company.swapCutoffHours ?? DEFAULT_SWAP_CUTOFF_HOURS,
});
const toPayload = (draft: ReturnType<typeof snapshot>) => draft;

/** Settings › Team: who approves shift swaps, and how close to a visit one may still be asked. */
export default function ShiftSwapPolicyCard() {
  const { t } = useTranslation();
  const form = useCompanyCardForm(snapshot, toPayload);
  if (!form.draft) return null;
  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="shift-swap-policy-heading">
      <div className="work-section-heading">
        <h2 id="shift-swap-policy-heading" className="mb-4 text-lg font-semibold">
          {t('shiftSwaps.settings.heading')}
        </h2>
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('shiftSwaps.settings.hint')}</p>
      <Select
        label={t('shiftSwaps.settings.policy')}
        value={form.draft.swapPolicy}
        disabled={form.readOnly || form.saving}
        error={form.fieldErrors.swapPolicy}
        options={SWAP_POLICIES.map((policy) => ({ value: policy, label: t(`shiftSwaps.settings.${policy}`) }))}
        onChange={(event) => form.set('swapPolicy', event.target.value as SwapPolicy)}
      />
      <NumberInput
        label={t('shiftSwaps.settings.cutoff')}
        hint={t('shiftSwaps.settings.cutoffHint')}
        min={0}
        max={MAX_SWAP_CUTOFF_HOURS}
        step={1}
        value={form.draft.swapCutoffHours}
        disabled={form.readOnly || form.saving || form.draft.swapPolicy === 'off'}
        error={form.fieldErrors.swapCutoffHours}
        onValueChange={(value) => form.set('swapCutoffHours', value ?? DEFAULT_SWAP_CUTOFF_HOURS)}
      />
      <CardSaveFooter form={form} saveLabel={t('shiftSwaps.settings.save')} />
    </section>
  );
}
